import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import mapValues from "lodash/mapValues";
import isUndefined from "lodash/isUndefined";
import reduce from "lodash/reduce";

import Length from "./../../../../source/js/shared/models/Geometry/Length";

import {isModelInsideParent} from "@/functions/makerjs";
import ShapeValidator from "@/utilities/ShapeValidator";
import {CutoutTypes} from "@/Models/CutoutTypes";
import Cutout from "@/Models/Cutout";
import {convertUnits, endsWithSeparator} from "@/functions/numbers";

function getInitialDimensions(): object {
	const enabledShapes = window.measurementSelectorVariables.enabledShapes;
	const dimensions = {};

	// always add text as a cutout option
	dimensions[ 'text' ] = {};

	enabledShapes.forEach( ( enabledShape ) => {
		dimensions[ enabledShape ] = {};
	} );

	return dimensions;
}

export default {
	state: (): object => ( {
		cutoutType: CutoutTypes.none,
		cutoutShape: '',
		cutoutOffsetLeft: 0,
		cutoutOffsetBottom: 0,
		cutoutRotation: 0,
		cutoutMeasurements: {
			dimensions: getInitialDimensions(),
			modifiers: {}
		},
		defaultCutoutDimensions: true,
		cutouts: [],
		currentCutoutIndex: 0,
	} ),
	mutations: {
		updateCutoutType( state, cutoutType: CutoutTypes ): void {
			state.cutoutType = cutoutType;
		},
		updateCutoutOffsetLeft( state, cutoutOffsetLeft: number ): void {
			if ( state.currentCutoutIndex !== -1 && ! isUndefined( state.cutouts[state.currentCutoutIndex] ) ) {
				state.cutouts[state.currentCutoutIndex].cutoutOffsetLeft = cutoutOffsetLeft;
			}

			state.cutoutOffsetLeft = cutoutOffsetLeft;
		},
		updateCutoutOffsetBottom( state, cutoutOffsetBottom: number ): void {
			if ( state.currentCutoutIndex !== -1 && ! isUndefined( state.cutouts[state.currentCutoutIndex] ) ) {
				state.cutouts[state.currentCutoutIndex].cutoutOffsetBottom = cutoutOffsetBottom;
			}

			state.cutoutOffsetBottom = cutoutOffsetBottom;
		},
		updateCutoutRotation( state, cutoutRotation: number ): void {
			if ( state.currentCutoutIndex !== -1 && ! isUndefined( state.cutouts[state.currentCutoutIndex] ) ) {
				state.cutouts[state.currentCutoutIndex].cutoutRotation = cutoutRotation;
			}

			state.cutoutRotation = cutoutRotation;
		},
		updateCutoutShape( state, cutoutShape: string ): void {
			state.cutoutShape = cutoutShape;

			// Remove any modifiers that may have been set
			state.cutoutMeasurements.modifiers = {};
		},
		updateCutoutDimension( state, { dimension, value } ): void {

			// When switching from Text back to a shape, cutout fails. Setting it to a default shape fixes this.
			if ( state.cutoutShape === 'none' ) {
				state.cutoutShape = 'rectangle';
			}

			if ( typeof value === 'string' ) {
				value = value.replace( ',', '.' );
			}

			// Using reduce is much more performant than using the spread operation on large objects (in some cases 5+ seconds).
			state.cutoutMeasurements.dimensions = reduce(state.cutoutMeasurements.dimensions, (result: object, currentValue: object, shape: string) => {

				// Create a shallow copy of the accumulator to avoid modifying the original object
				const newResult = { ...result };

				if (shape === state.cutoutShape) {

					// Create a shallow copy of currentValue to avoid modifying the original object
					const newValue = { ...currentValue };
					newValue[dimension] = Math.round(value * 10) / 10;
					newResult[shape] = newValue;
				} else {

					// If the shape doesn't match, keep the original currentValue
					newResult[shape] = currentValue;
				}

				return newResult;
			}, {});
		},
		setCutoutDimensionsForShape( state, { shape, dimensions } ): void {
			state.cutoutMeasurements.dimensions = { ...state.cutoutMeasurements.dimensions, ...{
				[shape]: dimensions
			} };
		},
		setCutoutModifiers( state, { modifiers } ): void {
			state.cutoutMeasurements.modifiers = { ...state.cutoutMeasurements.modifiers, ...modifiers };
		},
		resetCutoutDimension( state, shape: string ): void {
			state.cutoutMeasurements.dimensions[ shape ] = {};
		},
		updateCutoutModifier( state, { modifier, value } ): void {
			state.cutoutMeasurements.modifiers[modifier] = value;
		},
		updateUsingDefaultCutoutDimensions: ( state, usesDefaults ): void => state.defaultCutoutDimensions = usesDefaults,
		addCutout( state, cutout: Cutout ): void {
			state.cutouts = [...state.cutouts, cutout];
		},
		updateCutouts( state, cutouts ): void {
			state.cutouts = cutouts;
		},
		updateCutoutIndex( state, index ): void {
			state.currentCutoutIndex = index;
		},
		updateCutoutByIndex( state, { index, cutout }: { index: number, cutout: Cutout } ): void {

			// Remove the cutout at the index and
			// replace it with the new cutout to trigger a 'change' in the cutouts array
			state.cutouts = [
				...state.cutouts.slice(0, index),
				cutout,
				...state.cutouts.slice(index + 1)
			];
		},
		removeCutoutByIndex( state, index ): void {

			// Remove the cutout at the index and replace the cutouts array to trigger a 'change'
			state.cutouts = [
				...state.cutouts.slice(0, index),
				...state.cutouts.slice(index + 1)
			];
		}
	},
	getters: {
		cutoutByIndex: ( state ): Function => ( index: number ) => state.cutouts[ index ],
		currentCutout: ( state, getters ): Cutout => getters.cutoutByIndex( state.currentCutoutIndex ),
		cutoutOffsetLeft: ( state ): number => state.cutoutOffsetLeft,
		cutoutOffsetBottom: ( state ): number => state.cutoutOffsetBottom,
		cutoutRotation: ( state ): number => state.cutoutRotation,
		cutouts: ( state ): Cutout[] => state.cutouts,
		currentCutoutIndex: ( state ): number => state.currentCutoutIndex,
		cutoutType: ( state ): CutoutTypes => state.cutoutType,
		cutoutShape: ( state ): string => state.cutoutShape,
		cutoutMeasurements: ( state ): object => state.cutoutMeasurements,
		cutoutModifiers: ( state ): object => state.cutoutMeasurements.modifiers,
		cutoutDimensions: ( state, { cutoutShape } ): object => state.cutoutMeasurements.dimensions[ cutoutShape ],
		cutoutPrimitives: ( state, getters, rootState ): object => {
			if ( getters.cutoutShape in rootState.shapeState ) {
				return getters[ `${getters.cutoutShape}/cutoutPrimitives` ];
			}

			return {
				...convertUnits( getters.cutoutMeasurements.dimensions[ getters.cutoutShape ] ),
				...getters.cutoutMeasurements.modifiers,
			};
		},
		selectedCutoutsPreview: ( state, getters ): string => {
			const cutouts = getters.cutouts;
			const shapes = [];
			const i18n = window.measurementSelectorVariables.i18n;

			for ( const cutout of cutouts ) {
				shapes.push(i18n.shapeNames[cutout.shape]);
			}
			return shapes.join(', ');
		},
		shapeSupportCutoutRotation: ( state, getters ): boolean => {
			return !['circle'].includes( getters.cutoutShape );
		},
		cutout: ( { cutoutType }, getters ): any => {
			if ( cutoutType === CutoutTypes.none ) {
				return null;
			}

			const { cutoutShape, cutoutMeasurements, shapeInstance, cutoutOffsetLeft, cutoutOffsetBottom, cutoutRotation} = getters;

			const shapeValidator = new ShapeValidator();

			if ( !shapeValidator.isShapeComplete( cutoutShape, cutoutMeasurements ) ) {
				return null;
			}

			const instance = shapeInstance( cutoutShape, cutoutMeasurements );

			if ( isUndefined( instance ) ) {
				return null;
			}

			const { dimensions, modifiers } = cutoutMeasurements;

			const cutoutInstance = new Cutout( cutoutShape, dimensions, modifiers, {
				cutoutOffsetLeft,
				cutoutOffsetBottom,
				rotation: cutoutRotation,
			}, instance );

			return { ...cutoutInstance };
		},
		shouldApplyFillet(): boolean {
			const filletSharpEdges = window.measurementSelectorVariables.pricingGroup.other_settings.fillet_sharp_edges;

			return ! isNil( filletSharpEdges ) && !!filletSharpEdges;
		},
		filletSize(state, getters, rootState, rootGetters): Length {
			if (rootGetters.activeEdgeFinish === 'lasering') {
				return new Length(1);
			}
			return rootGetters.minimumDrillHoleDiameterLength;
		},
		getCutoutArea: ( state, getters, rootState ): number => {
			if ( getters.cutoutShape in rootState.shapeState ) {
				return getters[ `${getters.cutoutShape}/getCutoutArea` ];
			}

			if ( isEmpty( getters.cutoutPrimitives ) ) {
				return 0;
			}

			return getters.getArea(
				getters.cutoutShape,
				getters.cutoutMeasurements.dimensions[ getters.cutoutShape ],
				getters.cutoutMeasurements.modifiers
			);
		},
		cutoutsConfig: ( state, getters ) :{
			type: string;
			shape: string;
			primitives: {};
			position: {};
			modifiers: {};
		}[] => {
			const cutouts = getters.cutouts;

			if ( cutouts.length === 0 ) {
				return [];
			}

			if( cutouts[0] === null ) {
				return [];
			}

			// Possible to add more cutouts with a different type
			return cutouts.map((cutout: Cutout) => {
				return {
					type: 'shape',
					shape: cutout.shape,
					primitives: cutout.dimensions,
					modifiers: cutout.modifiers,
					position: {
						left: cutout.offsets[0].in_mm(),
						bottom: cutout.offsets[1].in_mm(),
						rotation: cutout.attributes.rotation,
					},
				};
			});
		},
		cutoutConfig: ( state, getters ): object => {
			const data = {
				type: getters.cutoutType
			};

			if( getters.cutoutType !== CutoutTypes.none ) {
				data[ 'shape' ] = getters.cutoutShape;
				data[ 'primitives' ] = getters.cutoutPrimitives;
				data[ 'position' ] = {
					left: state.cutoutOffsetLeft,
					bottom: state.cutoutOffsetBottom,
					rotation: state.cutoutRotation,
				};
			}
			return data;
		},
		cutoutCenterPosition: ( { cutoutType }, { cutoutPrimitives, cutout, getCenter } ): { x: number, y: number } => {
			if ( cutoutType === CutoutTypes.none || isEmpty( cutoutPrimitives ) || isEmpty( cutout ) ) {
				return { x: 0, y: 0 };
			}

			return getCenter();
		},
		getEnabledCutoutShapesForPricingGroup(): string[] {
			return window.measurementSelectorVariables.pricingGroup.enabled_shapes;
		},
		usesDefaultCutoutDimensions: ( state ): boolean => state.defaultCutoutDimensions,

		/**
		 * @deprecated
		 */
		cutoutOutsideModel: ( state, getters ): boolean => {
			const completeModel = getters.getExportableModel;
			return ! isUndefined( completeModel ) && ! isUndefined( completeModel.models.cutout ) && ! isModelInsideParent( completeModel.models.cutout, completeModel.models.baseModel, true );
		},
		isCutoutsVisible: ( state, getters ): boolean => {
			if(!getters.shape) return false;

			return getters.isCutoutsEnabled && getters.shape !== 'dxf' && getters.shape !== 'text';
		},
	},
	actions: {
		updateCurrentCutout( { commit, getters } ): void {
			const cutout = getters.currentCutout;

			commit( 'updateCutoutShape', cutout.shape );
			commit( 'updateCutoutOffsetLeft', cutout.offsets[0].in_mm() );
			commit( 'updateCutoutOffsetBottom', cutout.offsets[1].in_mm() );
			commit( 'updateCutoutRotation', cutout.attributes.rotation );
			commit( 'setCutoutDimensionsForShape', { shape: cutout.shape, dimensions: cutout.dimensions } );
			commit( 'setCutoutModifiers', { modifiers: cutout.modifiers } );
		},
		async removeCutout({ commit, getters, dispatch }, index: number): Promise<void> {
			await commit('removeCutoutByIndex', index);
			await commit('updateCutoutIndex', getters.cutouts.length - 1);

			if(getters.cutouts.length === 0) {
				commit( 'updateCutoutType', 'none' );
			} else {
				dispatch( 'updateCurrentCutout' );
			}
		},
		async resetCutoutShapeAndDimension( {dispatch, commit, getters}, shape ): Promise<void> {
			commit( 'updateCutoutShape', shape );
			await dispatch('updateDefaultCutoutMeasurements');

			if ( shape === 'rounded-rectangle' ) {
				commit( 'updateCutoutModifier', { modifier: 'use_individual_corners', value: getters[ 'rounded-rectangle/useIndividualCorners' ] ?? false } );
			}

			await dispatch( 'updateCutoutPosition' );

			commit( 'updateUsingDefaultCutoutDimensions', true );
		},
		async updateCutoutPosition( { commit, getters } ): Promise<void> {
			const position = getters.cutoutCenterPosition;

			commit( 'updateCutoutOffsetLeft', position.x );
			commit( 'updateCutoutOffsetBottom', position.y );
		},
		async updateCutoutDimension( { commit }, { dimension, value } ): Promise<void> {
			if ( endsWithSeparator( value ) ) {
				return;
			}

			commit( 'updateCutoutDimension', { dimension, value });
		},
		async updateCutoutsFromConfig( { commit, getters, dispatch }, cutouts ): Promise<void> {
			commit('updateCutouts', []);

			for ( const cutout of cutouts ) {

				commit( 'updateCutoutType', cutout.type );

				// If text, collect the fonts
				if ( cutout.shape === 'text' ) {
					await dispatch('loadFont', cutout.modifiers.font );
				}

				const instance = getters.shapeInstance(cutout.shape, {
					dimensions: { [cutout.shape] : cutout.primitives },
					modifiers: cutout.modifiers,
				} );

				commit('addCutout', new Cutout(
					cutout.shape,
					{ [cutout.shape] : cutout.primitives },
					cutout.modifiers,
					{
						cutoutOffsetLeft: new Length(cutout.position.left).round().in_mm(),
						cutoutOffsetBottom: new Length(cutout.position.bottom).round().in_mm(),
						rotation: cutout.position.rotation,
					}, instance
				));
			}

			commit('updateCutoutIndex', getters.cutouts.length - 1);
			dispatch('updateCurrentCutout');
		},
		updateDefaultCutoutMeasurements( { commit, getters, dispatch } ): void {
			const parentShape = getters.shape;
			const cutoutShape = getters.cutoutShape;
			const cutoutMeasurements = getters.cutoutMeasurements.dimensions[cutoutShape];

			if ( ! parentShape || ! cutoutShape ) {
				return;
			}

			const parentShapeObject = getters.shapeInstance( parentShape, {
				dimensions: getters.measurements.dimensions,
				modifiers: getters.modifiers,
			} );

			const cutoutShapeObject = getters.shapeInstance( cutoutShape, {
				dimensions: cutoutMeasurements.dimensions,
				modifiers: cutoutMeasurements.modifiers,
			} );

			commit( 'setCutoutModifiers', {
				shape: cutoutShape,
				modifiers: cutoutShapeObject.getDefaultCutoutModifiers()
			} );

			const cutoutDimensions = cutoutShapeObject.getDefaultCutoutDimensions(parentShapeObject);

			// Map the values to ensure we always have valid numbers.
			mapValues( cutoutDimensions, ( val: number, key: string ) => {
				cutoutDimensions[key] = Math.round( val * 10 ) / 10;
			} );

			commit('setCutoutDimensionsForShape', {
				shape: cutoutShape,
				dimensions: cutoutDimensions,
			});

			dispatch('updateCutoutPosition');
		},
		async resetCutouts( { commit, dispatch } ): Promise<void> {
			commit( 'updateCutoutType', CutoutTypes.none );
			commit( 'updateCutouts', [] );
			commit( 'updateCutoutIndex', 0 );

			await dispatch( 'resetCutoutShapeAndDimension', '' );
		}
	}
};
